import React from 'react';
import { useRecoilValue } from 'recoil';
import { environmentContextState, toViewPriceFnState } from '../../recoil/atoms';
import { ShopifyLineItem } from '../../models/ShopifyLineItem.model';
import { sizeImage } from '../../util';
import { ProductCategory } from '../../enums/ProductCategory.enum';
import classNames from 'classnames';
import { noReturnLineItemPropertyKey, useAdjustCartQuantityAction } from '../../hooks/actions/useCartState';
import { productsState } from '../../recoil/selectors';
import { Preorder } from '../../models/Preorder.model';
import { DivButton } from '../../root/components/DivButton';
import { Direction, useProductAssets } from '../../hooks/useProductAssets';
import { blankImage } from '../../product_detail/components/ImageLoader';
import { Pills } from '../../components/Pills';
import styles from './CartItem.module.scss';

interface Props {
  lineItem: ShopifyLineItem;
  preorder?: Preorder;
  decrementOverride?: () => void;
  incrementOverride?: () => void;
}

export const CartItem: React.FC<Props> = ({ lineItem, preorder, decrementOverride, incrementOverride }) => {
  const adjustCartQuantity = useAdjustCartQuantityAction();
  const toViewPriceFn = useRecoilValue(toViewPriceFnState);
  const displayVariant = (li: ShopifyLineItem) => li.variant_options.join(' • ');
  const products = useRecoilValue(productsState);
  const product = lineItem.handle && products[lineItem.handle];
  const variant = product.variants.find((v) => lineItem.sku === v.sku);
  const finalLinePrice = lineItem.final_line_price;
  const lineItemReturnable = !lineItem.properties[noReturnLineItemPropertyKey];

  const photos = useProductAssets(product);
  const filteredMannequinPhotos = photos.mannequinPhotos.filter(({ color, direction }) => color === variant?.color && direction === Direction.Front);
  const filteredModelPhotos = photos.modelPhotos.filter(({ color }) => color === variant?.color);
  const defaultPhoto = filteredMannequinPhotos[0] ?? filteredModelPhotos[0] ?? { src: lineItem.image } ?? { src: blankImage };
  const mainPhoto = lineItem.properties._bundleHandle ? { src: lineItem.image } : defaultPhoto;
  const mainPhotoSrc = sizeImage(mainPhoto.src, '140x177');

  const decrementCartQuantity = decrementOverride ? decrementOverride : () => adjustCartQuantity(lineItem, -1);
  const incrementCartQuantity = incrementOverride ? incrementOverride : () => adjustCartQuantity(lineItem, 1);

  return (
    <div className={styles.hlCartItem}>
      <div className={styles.hlCartProduct}>
        <div className={styles.hlCartThumbnail}>
          <img alt="product" className={styles.hlCartItemImage} src={mainPhotoSrc} />
        </div>
        <div className={styles.hlCartItemDetails}>
          {lineItem.product_type === ProductCategory.AddOns && <div className={styles.hlMiscItemType}>{lineItem.product_title}</div>}
          {lineItem.product_type !== ProductCategory.AddOns && <div className={styles.hlCartItemType}>{lineItem.product_title}</div>}
          <div className={styles.hlItemSubtype}>{displayVariant(lineItem)}</div>
          {preorder?.date && <div className={styles.preOrder}>{preorder.preorderText}</div>}
          <div className={styles.hlItemCartControls}>
            <DivButton className={styles.hlCartIncrement} label={`Decrement quantity`} onClick={decrementCartQuantity}>
              -
            </DivButton>
            <div className={styles.hlItemCount}>{lineItem.quantity}</div>
            <DivButton className={classNames(styles.hlCartIncrement, styles.plus)} label={`Increment quantity`} onClick={incrementCartQuantity}>
              +
            </DivButton>
          </div>
        </div>
      </div>
      <div className={styles.hlItemPrice}>
        {finalLinePrice < lineItem.original_line_price && <span className={styles.crossout}>{toViewPriceFn(lineItem.original_line_price)}</span>}
        <span>{toViewPriceFn(finalLinePrice)}</span>
        <Pills
          style={{ marginBottom: '0', marginTop: '4px' }}
          pillStyle={product?.isReturnable && lineItemReturnable ? {} : { backgroundColor: '#A193ED', color: 'white' }}
          items={product?.isReturnable && lineItemReturnable ? ['Free returns'] : ['Final sale']}
        />
      </div>
    </div>
  );
};

export default CartItem;
